/**
 * Country-total trend chart for Compare mode: resolves the tracker campaign/provider from
 * the country data and draws that tracker's trend (TrackerTrendChart) — Voluum, Binom or
 * Tres Commas, each from its own series (provider=voluum|binom|tres-commas).
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo } from 'react';

import { useCountry } from '@/hooks';
import { statsTrackerId } from '@/features/trackers';
import type { DashboardDateRange, Granularity } from '@/features/dashboard/compare/data/types';

import { TrackerTrendChart } from './TrackerTrendChart';

interface Campaign {
  id: number;
  trafficCampaignId?: string | null;
  voluumCampaignId?: string | null;
  trafficProvider?: string | null;
}

function resolveCampaign(campaigns: Campaign[] | undefined, wiserIds: number[]) {
  const c = (campaigns ?? []).find((x) => wiserIds.includes(x.id));
  const rawId = c?.trafficCampaignId ?? c?.voluumCampaignId;
  const campaignId = rawId != null && String(rawId).trim() ? String(rawId).trim() : undefined;
  return { campaignId, tracker: statsTrackerId(c?.trafficProvider) };
}

interface StatsCompareChartProps {
  countryKey: string;
  wiserIds: number[];
  rangeA: DashboardDateRange;
  rangeB: DashboardDateRange;
  granularity: Granularity;
}

export function StatsCompareChart({ wiserIds, ...chart }: StatsCompareChartProps) {
  const country = useCountry(chart.countryKey);
  const { campaignId, tracker } = useMemo(
    () => resolveCampaign(country.data?.campaigns as Campaign[] | undefined, wiserIds),
    [country.data?.campaigns, wiserIds],
  );

  return <TrackerTrendChart {...chart} campaignId={campaignId} provider={tracker} />;
}

export default StatsCompareChart;
